import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography
} from "@material-ui/core";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import Dialog from "../../../waybee-ui/Dialog";
import DialogTitle from "../../../waybee-ui/Dialog/DialogTitle";
import DialogContent from "../../../waybee-ui/Dialog/DialogContent";
import DialogActions from "../../../waybee-ui/Dialog/DialogActions";
import Button from "../../../waybee-ui/Button";
import {
  HIGHLIGHT_APPLY_KINDS,
  HIGHLIGHT_BASE_KINDS,
  HIGHLIGHT_COLORS,
  HIGHLIGHT_OPERATORS,
  applyKindFromBase,
  createEmptyHighlight,
  isDesistenteKind,
  normalizeHighlight,
  operatorRequiresValue,
  operatorRequiresValueTo,
  HIGHLIGHT_GRAY
} from "../../../utils/generalBoardHighlights";

const Field = styled(TextField)`
  && {
    margin-top: 12px;
  }
`;

const SelectField = styled(FormControl)`
  && {
    margin-top: 12px;
    min-width: 100%;
  }
`;

const ColorRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 0;
`;

const ColorSwatch = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 8px;
  border: 2px solid ${({ $selected }) => ($selected ? "#333" : "#ddd")};
  background: ${({ $color }) => $color};
  cursor: pointer;
  padding: 0;
`;

const Hint = styled(Typography)`
  && {
    color: #777;
    font-size: 13px;
    margin-top: 4px;
  }
`;

const SectionLabel = styled(Typography)`
  && {
    margin: 0 0 4px;
    line-height: 1.3;
  }
`;

const Warning = styled(Typography)`
  && {
    color: #b26a00;
    font-size: 13px;
    margin-top: 8px;
  }
`;

const ColumnsBox = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 8px;
  padding-left: 8px;
`;

const APPLY_KIND_VISIBILITY = {
  callNumber: "callNumber",
  ra: "ra",
  name: null,
  absencesDiscipline: "absencesDiscipline",
  absencesTotal: "absencesTotal",
  absencesPercentDiscipline: "absencesPercentDiscipline",
  absencesPercentTotal: "absencesPercentTotal",
  gradeAny: "grades",
  gradeAverage: "grades"
};

const emptyDraft = () =>
  createEmptyHighlight({
    base: { kind: "absencesPercentDiscipline" },
    operator: "gte",
    color: HIGHLIGHT_COLORS[3]
  });

const HighlightRuleDialog = ({
  open,
  rule,
  saving,
  columnVisibility,
  onClose,
  onSave
}) => {
  const { t } = useTranslation();
  const [draft, setDraft] = useState(emptyDraft);

  useEffect(() => {
    if (!open) return;
    setDraft(rule ? normalizeHighlight(rule, rule.order || 0) : emptyDraft());
  }, [open, rule]);

  const needsValue = operatorRequiresValue(draft.operator);
  const needsValueTo = operatorRequiresValueTo(draft.operator);
  const needsGradeName = draft.base && draft.base.kind === "gradeByName";
  const isDesistenteBase = isDesistenteKind(draft.base && draft.base.kind);
  const gradeName =
    needsGradeName && draft.base.name ? String(draft.base.name).trim() : "";
  const applyEntireRow = !draft.applyTo || draft.applyTo.mode !== "columns";
  const selectedColumns = (draft.applyTo && draft.applyTo.columns) || [];
  const hiddenApplyKinds = useMemo(() => {
    if (applyEntireRow) return [];
    return selectedColumns.filter(kind => {
      const visibilityKey = APPLY_KIND_VISIBILITY[kind];
      if (!visibilityKey) return false;
      return columnVisibility && columnVisibility[visibilityKey] === false;
    });
  }, [applyEntireRow, columnVisibility, selectedColumns]);

  const applyKindLabel = kind => {
    if (kind === "gradeAny" && gradeName) {
      return t("settings.generalBoard.highlights.kinds.gradeAnyNamed", {
        name: gradeName
      });
    }
    return t(`settings.generalBoard.highlights.kinds.${kind}`);
  };

  const canSave = useMemo(() => {
    if (!draft || !draft.base || !draft.base.kind) return false;
    if (needsGradeName && !(draft.base.name || "").trim()) return false;
    if (!isDesistenteBase) {
      if (needsValue && !(draft.value || "").trim()) return false;
      if (needsValueTo && !(draft.valueTo || "").trim()) return false;
    }
    if (!applyEntireRow && !selectedColumns.length) return false;
    return Boolean(draft.color);
  }, [
    applyEntireRow,
    draft,
    isDesistenteBase,
    needsGradeName,
    needsValue,
    needsValueTo,
    selectedColumns.length
  ]);

  const updateDraft = patch => {
    setDraft(current => ({ ...current, ...patch }));
  };

  const handleBaseKindChange = event => {
    const kind = event.target.value;
    if (isDesistenteKind(kind)) {
      updateDraft({
        base: { kind },
        operator: "eq",
        value: "S",
        valueTo: "",
        color: draft.color || HIGHLIGHT_GRAY,
        applyTo: { mode: "row", columns: [] }
      });
      return;
    }
    const base =
      kind === "gradeByName"
        ? { kind, name: (draft.base && draft.base.name) || "" }
        : { kind };
    if (applyEntireRow) {
      updateDraft({ base });
      return;
    }
    updateDraft({
      base,
      applyTo: {
        mode: "columns",
        columns: selectedColumns.length
          ? selectedColumns
          : [applyKindFromBase(kind)]
      }
    });
  };

  const handleApplyRowChange = event => {
    if (event.target.checked) {
      updateDraft({ applyTo: { mode: "row", columns: [] } });
      return;
    }
    const baseKind = (draft.base && draft.base.kind) || "name";
    updateDraft({
      applyTo: {
        mode: "columns",
        columns: selectedColumns.length
          ? selectedColumns
          : [applyKindFromBase(baseKind)]
      }
    });
  };

  const handleApplyColumnToggle = key => event => {
    const { checked } = event.target;
    const columns = checked
      ? selectedColumns
          .concat(key)
          .filter((item, index, list) => list.indexOf(item) === index)
      : selectedColumns.filter(item => item !== key);
    updateDraft({ applyTo: { mode: "columns", columns } });
  };

  const handleSubmit = () => {
    if (saving) return;
    const normalized = normalizeHighlight(draft, draft.order || 0);
    if (!normalized) return;
    onSave(normalized);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      loading={saving}
    >
      <DialogTitle>
        {rule
          ? t("settings.generalBoard.highlights.editTitle")
          : t("settings.generalBoard.highlights.addTitle")}
      </DialogTitle>
      <DialogContent>
        <SelectField>
          <InputLabel id="highlight-base-label">
            {t("settings.generalBoard.highlights.baseColumn")}
          </InputLabel>
          <Select
            labelId="highlight-base-label"
            value={(draft.base && draft.base.kind) || ""}
            onChange={handleBaseKindChange}
          >
            {HIGHLIGHT_BASE_KINDS.map(kind => (
              <MenuItem key={kind} value={kind}>
                {t(`settings.generalBoard.highlights.kinds.${kind}`)}
              </MenuItem>
            ))}
          </Select>
        </SelectField>

        {needsGradeName && (
          <Field
            label={t("settings.generalBoard.highlights.gradeName")}
            value={(draft.base && draft.base.name) || ""}
            onChange={event => {
              updateDraft({
                base: { kind: "gradeByName", name: event.target.value }
              });
            }}
            fullWidth
            helperText={t("settings.generalBoard.highlights.gradeNameHint")}
          />
        )}

        {!isDesistenteBase && (
          <SelectField>
            <InputLabel id="highlight-operator-label">
              {t("settings.generalBoard.highlights.operator")}
            </InputLabel>
            <Select
              labelId="highlight-operator-label"
              value={draft.operator || ""}
              onChange={event => updateDraft({ operator: event.target.value })}
            >
              {HIGHLIGHT_OPERATORS.map(operator => (
                <MenuItem key={operator} value={operator}>
                  {t(`settings.generalBoard.highlights.operators.${operator}`)}
                </MenuItem>
              ))}
            </Select>
          </SelectField>
        )}

        {isDesistenteBase && (
          <Hint>
            {t("settings.generalBoard.highlights.desistenteHint")}
          </Hint>
        )}

        {!isDesistenteBase && needsValue && (
          <Field
            label={t("settings.generalBoard.highlights.value")}
            value={draft.value || ""}
            onChange={event => updateDraft({ value: event.target.value })}
            fullWidth
          />
        )}

        {!isDesistenteBase && needsValueTo && (
          <Field
            label={t("settings.generalBoard.highlights.valueTo")}
            value={draft.valueTo || ""}
            onChange={event => updateDraft({ valueTo: event.target.value })}
            fullWidth
          />
        )}

        <Box mt={2}>
          <SectionLabel variant="subtitle2">
            {t("settings.generalBoard.highlights.color")}
          </SectionLabel>
          <ColorRow>
            {HIGHLIGHT_COLORS.map(color => (
              <ColorSwatch
                key={color}
                type="button"
                $color={color}
                $selected={draft.color === color}
                aria-label={color}
                onClick={() => updateDraft({ color })}
              />
            ))}
          </ColorRow>
        </Box>

        <Box mt={2}>
          <SectionLabel variant="subtitle2">
            {t("settings.generalBoard.highlights.applyColumnsTitle")}
          </SectionLabel>
          <FormControlLabel
            style={{ marginTop: 0, marginBottom: 0 }}
            control={(
              <Checkbox
                color="primary"
                checked={applyEntireRow}
                onChange={handleApplyRowChange}
              />
            )}
            label={t("settings.generalBoard.highlights.entireRow")}
          />
          {!applyEntireRow && (
            <>
              <Hint>{t("settings.generalBoard.highlights.columnsHint")}</Hint>
              <ColumnsBox>
                {HIGHLIGHT_APPLY_KINDS.map(kind => (
                  <FormControlLabel
                    key={kind}
                    control={(
                      <Checkbox
                        color="primary"
                        checked={selectedColumns.includes(kind)}
                        onChange={handleApplyColumnToggle(kind)}
                      />
                    )}
                    label={applyKindLabel(kind)}
                  />
                ))}
              </ColumnsBox>
              {hiddenApplyKinds.length > 0 && (
                <Warning>
                  {t("settings.generalBoard.highlights.hiddenTargetsWarning", {
                    columns: hiddenApplyKinds
                      .map(kind => applyKindLabel(kind))
                      .join(", ")
                  })}
                </Warning>
              )}
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button color="secondary" onClick={onClose} disabled={saving}>
          {t("common.actions.cancel")}
        </Button>
        <Button
          color="primary"
          variant="contained"
          onClick={handleSubmit}
          disabled={!canSave || saving}
        >
          {saving ? t("common.actions.saving") : t("common.actions.save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

HighlightRuleDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  rule: PropTypes.object,
  saving: PropTypes.bool,
  columnVisibility: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired
};

HighlightRuleDialog.defaultProps = {
  rule: null,
  saving: false,
  columnVisibility: null
};

export default HighlightRuleDialog;
